import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { buildEpcPayload, type BankProfile } from '@core/validators/epcQr'

/**
 * The EPC (SEPA Credit Transfer) payment code.
 *
 * Generated ONLY from verified data — the builder refuses otherwise, and the
 * database refuses to hold an unverified IBAN in the first place. A scannable
 * code is a loaded weapon: whatever is in it gets paid without anyone retyping
 * it, so it must never come from a reading nobody has checked.
 *
 * Error correction M per the standard, and rendered large. A code that is
 * technically valid but small on a dim screen simply will not scan.
 */
export function PaymentQr({
  beneficiaryName,
  iban,
  amountCents,
  remittance,
  verified,
  profile,
}: {
  beneficiaryName: string
  iban: string
  amountCents: number
  remittance: string | null
  verified: boolean
  profile: BankProfile
}) {
  const [svg, setSvg] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    const result = buildEpcPayload({
      beneficiaryName,
      iban,
      amountCents,
      remittance,
      verified,
      profile,
    })

    if (!result.ok) {
      setSvg(null)
      setProblem(
        result.reason === 'not_verified'
          ? 'Eerst nakijken voor je een betaalcode krijgt'
          : 'Geen betaalcode mogelijk — gebruik de gegevens hieronder',
      )
      return
    }

    setProblem(null)
    void QRCode.toString(result.payload, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 1,
      color: { dark: '#1A1A1A', light: '#FFFFFF' },
    })
      .then(setSvg)
      .catch(() => setProblem('Kon de code niet tekenen'))
  }, [beneficiaryName, iban, amountCents, remittance, verified, profile])

  if (problem) {
    return (
      <div className="rounded-xl border border-line bg-canvas p-4 text-center">
        <p className="text-sm text-muted">{problem}</p>
      </div>
    )
  }

  if (!svg) return <div className="w-52 h-52 mx-auto rounded-xl bg-line animate-pulse" />

  return (
    <div className="text-center">
      <div
        className="w-52 h-52 mx-auto bg-white rounded-xl p-2 [&>svg]:w-full [&>svg]:h-full"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      <p className="text-xs text-muted mt-2">Scan met je bank-app</p>
    </div>
  )
}
